const {
  REACT_APP_HOST_ENV,
  REACT_APP_URL_API_DEVELOPMENT,
  REACT_APP_URL_API_PRODUCTION,
  REACT_APP_URL_API_STAGING,
  REACT_APP_URL_PUBLIC_DEVELOPMENT,
  REACT_APP_URL_API_VERSION,
  REACT_APP_URL_ASSETS,
  REACT_APP_URL_PUBLIC_PRODUCTION,
  REACT_APP_URL_NEW_DEVELOPMENT,
  REACT_APP_URL_NEW_PRODUCTION,
  REACT_APP_URL_NEW_STAGING,
} = process.env;

const origin =
  window &&
  window.location &&
  `${window.location.protocol}//${window.location.hostname}` +
    (window.location.port ? `:${window.location.port}` : "");

const getEnvironment = origin => {
  if (!!REACT_APP_HOST_ENV) {
    return REACT_APP_HOST_ENV;
  } else {
    switch (origin) {
      case REACT_APP_URL_PUBLIC_DEVELOPMENT:
        return "development";
      case REACT_APP_URL_PUBLIC_PRODUCTION:
        return "production";
      default:
        return "staging";
    }
  }
};

const getApiDomain = environment => {
  switch (environment) {
    case "development":
      return REACT_APP_URL_API_DEVELOPMENT;
    case "production":
      return REACT_APP_URL_API_PRODUCTION;
    default:
      return REACT_APP_URL_API_STAGING;
  }
};

const getNewSiteUrl = environment => {
  switch (environment) {
    case "development":
      return REACT_APP_URL_NEW_DEVELOPMENT;
    case "production":
      return REACT_APP_URL_NEW_PRODUCTION;
    default:
      return REACT_APP_URL_NEW_STAGING;
  }
};

/**
 * `HOST_ENV`
 * * First, checks if an environment is specified in the .env configuration, then, if not, falls back checking if the origin URL matches the production URL
 * * Used to determine authentication is required to view listing data
 * @constant {string} hostenv
 * @default
 */
export const HOST_ENV = getEnvironment(origin);

/**
 * `RESTRICT_LISTINGS`
 * * Determine authentication is required to view listing data based on the current environment
 * @constant {string} hostenv
 * @default
 */
// export const RESTRICT_LISTINGS = HOST_ENV === "staging";
export const RESTRICT_LISTINGS = false;

/**
 * `URL_API_DOMAIN` - Retrieves `REACT_APP_URL_API_PRODUCTION`,
 * `REACT_APP_URL_API_STAGING`, and `REACT_APP_URL_ADMIN_PRODUCTION`
 * from .env and determines whether to use the production or staging API
 * if the current window.location matches the production client URL
 * @constant {string} apidomain
 * @default
 */
export const URL_API_DOMAIN = getApiDomain(HOST_ENV);

/**
 * `URL_API_BASE` - Adds the API version to `URL_API_DOMAIN`
 * @constant {string} apibase
 * @default
 */
export const URL_API_BASE = URL_API_DOMAIN + REACT_APP_URL_API_VERSION;

/**
 * `URL_API_ALERTS` - API endpoint for saved alerts
 * @constant {string} apialerts
 * @default
 */
export const URL_API_ALERTS = "/savedAlerts";

/**
 * `URL_API_AUTH_FORGOT` - API endpoint for sending a password reset email
 * @constant {string} apiauthforgot
 * @default
 */
export const URL_API_AUTH_FORGOT = "/auth/forgot-password";

/**
 * `URL_API_AUTH_RESET` - API endpoint for changing a password
 * @constant {string} apiauthreset
 * @default
 */
export const URL_API_AUTH_RESET = "/auth/reset-password";

/**
 * `URL_API_BUY` - API endpoint for listings
 * @constant {string} apibuy
 * @default
 */
export const URL_API_BUY = "/listings/buy";

/**
 * `URL_API_CATEGORIES` - API endpoint for listings
 * @constant {string} apicategories
 * @default
 */
export const URL_API_CATEGORIES = "/categories";

/**
 * `URL_API_CONTACT_US` - API endpoint for sending Contact Us email
 * @constant {string} apicontactus
 * @default
 */
export const URL_API_CONTACT_US = "/listings/contactus";

/**
 * `URL_API_CONSIGNMENT` - API endpoint for sending consignment request
 * @constant {string} apiconsignment
 * @default
 */
export const URL_API_CONSIGNMENT = "/consignment";

/**
 * `URL_API_EMAIL_SIGNUP` - API endpoint for email sign-up
 * @constant {string} apiemailsignup
 * @default
 */
export const URL_API_EMAIL_SIGNUP = "/newsletter-signup";

/**
 * `URL_API_LISTINGS` - API endpoint for listings
 * @constant {string} apilistings
 * @default
 */
export const URL_API_LISTINGS = "/listings";

/**
 * `URL_API_LISTINGS_COMPARE` - API endpoint for sending compared listings PDFs email
 * @constant {string} apilistingscompare
 * @default
 */
export const URL_API_LISTINGS_COMPARE = "/listings/compare";

/**
 * `URL_API_LISTINGS_COMPILATION` - API endpoint for sending the custom compilation email
 * @constant {string} apilistingscompilation
 * @default
 */
export const URL_API_LISTINGS_COMPILATION = "/listings/compilation";

/**
 * `URL_API_LISTINGS_CONTACT` - API endpoint for sending Contact Me About listings email
 * and Find This for Me email
 * @constant {string} apilistingscontact
 * @default
 */
export const URL_API_LISTINGS_CONTACT = "/listings/contact";

/**
 * `URL_API_LISTINGS_CSV_CATALOG` - API endpoint for downloading catalog listings CSV
 * @constant {string} apilistingscsvcatalog
 * @default
 */
export const URL_API_LISTINGS_CSV_CATALOG = `${URL_API_DOMAIN}/listings/csv/catalog`;

/**
 * `URL_API_LISTINGS_SELL_SHEET` - API endpoint for sending sell sheet email
 * @constant {string} apilistingssellsheet
 * @default
 */
export const URL_API_LISTINGS_SELL_SHEET = "/listings/sell-sheet";

/**
 * `URL_API_LISTINGS_SHARE` - API endpoint for sending share listings email
 * @constant {string} apilistingsshare
 * @default
 */
export const URL_API_LISTINGS_SHARE = "/listings/share";

/**
 * `URL_API_LISTINGS_PDF` - API endpoint for downloading listings PDF
 * @constant {string} apilistingspdf
 * @default
 */
export const URL_API_LISTINGS_PDF = `${URL_API_DOMAIN}/listings/pdf`;

/**
 * `URL_API_LISTINGS_PDF_ACTIVITY` - API endpoint for downloading activity report PDF
 * @constant {string} apilistingspdfactivity
 * @default
 */
export const URL_API_LISTINGS_PDF_ACTIVITY = "/listings/activity";

/**
 * `URL_API_LISTINGS_PDF_WEEKLY` - API endpoint for downloading weekly listings PDF
 * @constant {string} apilistingspdfweekly
 * @default
 */
export const URL_API_LISTINGS_PDF_WEEKLY = `${URL_API_DOMAIN}/listings/pdf/weekly`;

/**
 * `URL_API_LISTINGS_CSV_WEEKLY` - API endpoint for downloading weekly listings CSV
 * @constant {string} apilistingscsvweekly
 * @default
 */
export const URL_API_LISTINGS_CSV_WEEKLY = `${URL_API_DOMAIN}/listings/csv/weekly`;

/**
 * `URL_API_LISTINGS_PDF_COMPARE` - API endpoint for downloading compared listings PDF
 * @constant {string} apilistingspdfcompare
 * @default
 */
export const URL_API_LISTINGS_PDF_COMPARE = `${URL_API_DOMAIN}/listings/compare`;

/**
 * `URL_API_LIVE` - API endpoint for live listings
 * @constant {string} apilive
 * @default
 */
export const URL_API_LIVE = "/listings/live";

/**
 * `URL_API_LOGIN` - API endpoint for login
 * @constant {string} apilogin
 * @default
 */
export const URL_API_LOGIN = "/auth/login";

/**
 * `URL_API_LOGOUT` - API endpoint for logout
 * @constant {string} apilogout
 * @default
 */
export const URL_API_LOGOUT = "/auth/logout";

/**
 * `URL_API_MAKES_MODELS` - API endpoint for logout
 * @constant {string} apimakesmodels
 * @default
 */
export const URL_API_MAKES_MODELS = "/makes-models";

/**
 * `URL_API_PREQUALIFY` - API endpoint for resolving short link
 * @constant {string} apiprequalify
 * @default
 */
export const URL_API_PREQUALIFY = "/prequalify";

/**
 * `URL_API_SEND_FILES` - API endpoint for sending listing files email
 * @constant {string} apisendfiles
 * @default
 */
export const URL_API_SEND_FILES = "/sendFiles";

/**
 * `URL_API_SHORT_LINK` - API endpoint for resolving short link
 * @constant {string} apishortlink
 * @default
 */
export const URL_API_SHORT_LINK = "/sl";

/**
 * `URL_API_WATCH` - API endpoint for watched listings
 * @constant {string} apiwatch
 * @default
 */
export const URL_API_WATCH = "/watchedListings";

/**
 * `URL_ASSETS` - Retrieves `REACT_APP_URL_ASSETS` from .env
 * @constant {string} apiassets
 * @default
 */
export const URL_ASSETS = REACT_APP_URL_ASSETS;

/**
 * `URL_ORIGIN` - Base URL derived from the current window.location
 * @constant {string} origin
 * @default
 */
export const URL_ORIGIN = origin;

/**
 * `URL_NEW_SITE` - Retrieves `REACT_APP_URL_NEW_PRODUCTION` and
 * `REACT_APP_URL_NEW_STAGING` from .env and determines whether
 * to use the production or staging URL when linking to the
 * new equipment website if the current window.location matches
 * the production client URL
 * @constant {string} newsite
 * @default
 */
export const URL_NEW_SITE = getNewSiteUrl(HOST_ENV);

/**
 * `URL_PRODUCTION` - Base production URL
 * @constant {string} production
 * @default
 */
export const URL_PRODUCTION = REACT_APP_URL_PUBLIC_PRODUCTION;

/**
 * Get additional listing info API endpoint
 * @param {number} equipmentId
 * @param {string} dataType
 * @returns {string}
 */
export const getApiAdditionalListingInfo = (equipmentId, dataType) => {
  let url = `${URL_API_LISTINGS}/${equipmentId}/additional-info`;

  if (
    !!dataType &&
    ["coverageOptions", "inspections", "serviceHistory", "warranty"].includes(
      dataType
    )
  ) {
    url += `/${dataType}`;
  }

  return url;
};
